<template>
  <label
    :class="[
      $style.wrapper,
      {
        [$style['wrapper--checked']]: localGroupValue === value,
        [$style['wrapper--disabled']]: disabled,
      },
    ]"
  >
    <input
      type="radio"
      :class="$style.input"
      :name="name"
      :value="value"
      :checked="modelValue === value"
      :disabled="disabledValue"
      @change="emit('update:modelValue', value)"
    />
    <span
      :class="[
        $style.radio,
        {
          [$style['radio--checked']]: modelValue === value,
          [$style['radio--disabled']]: disabled,
        },
      ]"
    />
    <span
      :class="[
        $style.wrapper__text,
        {
          [$style['text--checked']]: localGroupValue === value,
          [$style['text--disabled']]: disabled,
        },
      ]"
    >
      <slot />
    </span>
  </label>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import type { OptionValueType } from '@/types/app'

const props = withDefaults(
  defineProps<{
    name: string
    modelValue?: OptionValueType
    value?: OptionValueType
    disabled?: boolean
  }>(),
  {
    modelValue: null,
    value: null,
    disabled: false,
  }
)

const disabledValue = computed(
  (): true | undefined => props.disabled || undefined
)

const emit = defineEmits<{
  (event: 'update:modelValue', value: OptionValueType): void
}>()

const localGroupValue = computed({
  get: () => props.modelValue,
  set: (newValue: typeof props.modelValue) =>
    emit('update:modelValue', newValue),
})
</script>

<style lang="scss" module>
.wrapper {
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid $color-border-low-emphasis;
  border-radius: $border-radius-rounder;
  background-color: $color-surface-primary;

  &--checked {
    background-color: $color-surface-accent-primary;
    border: 1px solid $color-surface-accent-primary;
  }

  &--disabled {
    background-color: $color-surface-primary;
    border: none;
  }

  &__text {
    &:nth-child(n + 2) {
      margin-left: 4px;
    }
  }
}

.text {
  &--checked {
    font-weight: 700;
    color: $color-text-and-object-high-emphasis-inverse;
  }

  &--disabled {
    color: $color-text-and-object-disable;
    font-weight: normal;
  }
}

.radiobutton {
  color: white;
}

.input {
  display: none;
}

.radio {
  $radio: &;
  $size: 20px;
  $color-on: $color-text-and-object-high-emphasis-inverse;
  $color-off: $color-border-medium-emphasis;
  $color-disabled: $color-text-and-object-disable;

  width: $size;
  height: $size;
  display: inline-block;
  position: relative;
  border: 2px solid $color-off;
  box-sizing: border-box;
  border-radius: 999px;

  &::before {
    $size: 10px;

    width: $size;
    height: $size;
    background-color: transparent;
    border-radius: 999px;
    margin: auto;
    display: inline-block;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    content: '';
  }

  &--checked {
    border-color: $color-on;

    &::before {
      background-color: $color-on;
    }

    &#{$radio}--disabled {
      border-color: $color-disabled;

      &::before {
        background-color: $color-disabled;
      }
    }

    &:focus:not(:focus-visible) {
      outline: none;
    }
  }

  &--disabled {
    border-color: $color-disabled;
  }
}
</style>
